// src/features/fb/cookies/questions/CookiesQuestions.jsx
import React, { useState, useRef } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import MaterialSelectionQuestion from "./MaterialSelectionQuestion";
import MaterialGradePlasticQuestion from "./MaterialGradePlasticQuestion";
import BatchSizeQuestion from "./BatchSizeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import PartDetailsQuestion from "./PartDetailsQuestion";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";

/**
 * CookiesQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {function} onDemoModeChange - Callback when demo mode changes
 * @param {object} initialData - Initial data to populate the form
 */
export default function CookiesQuestions({ onComplete, onDataChange, onDemoModeChange, initialData }) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      materialGrade: "",
      batchSize: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        regrindPct: "",
        avgWallThickness: "",
        runnerType: "",
        noOfCavities: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // State to track material validation status
  const [isMaterialValid, setIsMaterialValid] = useState(false);
  const [isValidatingMaterial, setIsValidatingMaterial] = useState(false);   // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 6; // Last question
    if (initialData.partDetails) return 5;
    if (initialData.partWeightDimensions) return 4;
    if (initialData.batchSize) return 3;
    if (initialData.materialGrade) return 2; // Material Grade
    if (initialData.materialSelection) return 1;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  const [demoMode, setDemoMode] = useState(false);

  const handleMaterialChange = (materialSelection) => {
    setProductFormData((previous) => ({
      ...previous,
      materialSelection,
      // A grade belongs to its base material. Never let an old grade override
      // a different material selected when returning to this question.
      materialGrade: previous.materialSelection?.trim().toUpperCase() === materialSelection.trim().toUpperCase()
        ? previous.materialGrade : "",
    }));
  };
  
  // Refs for validation
  const materialSelectionRef = useRef(null);
  const materialGradeRef = useRef(null);

  const isPositiveDecimal = (value) =>
    (typeof value === "number" || typeof value === "string") &&
    /^[+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(String(value).trim()) &&
    Number.isFinite(Number(value)) && Number(value) > 0;

  // Required model denominators and counts must be valid before pricing.
  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.materialSelection?.trim() &&
    isPositiveDecimal(productFormData.batchSize) && Number.isSafeInteger(Number(productFormData.batchSize)) &&
    isPositiveDecimal(productFormData.partWeightDimensions?.finishWeight) &&
    isPositiveDecimal(productFormData.partWeightDimensions?.partLength) &&
    isPositiveDecimal(productFormData.partWeightDimensions?.partWidth) &&
    isPositiveDecimal(productFormData.partWeightDimensions?.partHeight) &&
    isPositiveDecimal(productFormData.partDetails?.avgWallThickness) &&
    (productFormData.partDetails?.regrindPct == null || productFormData.partDetails.regrindPct === "" ||
      ((isPositiveDecimal(productFormData.partDetails.regrindPct) ||
        (typeof productFormData.partDetails.regrindPct === "number" && productFormData.partDetails.regrindPct === 0 ||
        typeof productFormData.partDetails.regrindPct === "string" && /^[+]?(?:0+(?:\.0*)?|\.0+)(?:e[+-]?\d+)?$/i.test(productFormData.partDetails.regrindPct.trim()))) && Number(productFormData.partDetails.regrindPct) <= 100)) &&
    (productFormData.partDetails?.noOfCavities == null || productFormData.partDetails.noOfCavities === "" ||
      (isPositiveDecimal(productFormData.partDetails.noOfCavities) &&
       Number.isSafeInteger(Number(productFormData.partDetails.noOfCavities)))) &&
    productFormData.manufacturingCountry?.trim();

  // Preserve the local draft, but never retain a price for invalid/reset inputs.
  React.useEffect(() => {
    onDataChange?.(isAllAnswered ? productFormData : null);
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display - only show values that have been answered
  const productSelectedValues = [
    productFormData.productName && {
      label: "Part Name:",
      value: productFormData.productName,
    },
    productFormData.materialSelection && {
      label: "Material:",
      // Show Material Grade if selected, otherwise show base material
      value: productFormData.materialGrade || productFormData.materialSelection,
    },
    productFormData.batchSize && {
      label: "Batch Size:",
      value: productFormData.batchSize,
    },
    // Only show Part Dimensions after step 3 (Part Weight Dimensions question) is answered
    productFormData.partWeightDimensions?.finishWeight &&
      productFormData.partWeightDimensions?.partLength &&
      productFormData.partWeightDimensions?.partWidth &&
      productFormData.partWeightDimensions?.partHeight && {
        label: "Part Dimensions:",
        value: `L: ${productFormData.partWeightDimensions.partLength}mm, W: ${
          productFormData.partWeightDimensions.partWidth
        }mm, H: ${productFormData.partWeightDimensions.partHeight}mm, Weight: ${
          productFormData.partWeightDimensions.finishWeight
        }g`,
      },
    // Show Part Details if any field has a value
    (productFormData.partDetails?.regrindPct ||
      productFormData.partDetails?.avgWallThickness ||
      productFormData.partDetails?.runnerType ||
      productFormData.partDetails?.noOfCavities) && {
      label: "Part Details:",
      value: [
        productFormData.partDetails?.regrindPct && `Regrind: ${productFormData.partDetails.regrindPct}%`,
        productFormData.partDetails?.avgWallThickness &&
          `Wall: ${productFormData.partDetails.avgWallThickness}mm`,
        productFormData.partDetails?.runnerType && `Runner: ${productFormData.partDetails.runnerType}`,
        productFormData.partDetails?.noOfCavities &&
          `Cavities: ${productFormData.partDetails.noOfCavities}`,
      ]
        .filter(Boolean)
        .join(", "),
    },
    productFormData.manufacturingCountry && {
      label: "Manufacturing Country:",
      value: productFormData.manufacturingCountry,
    },
  ].filter(Boolean); // Remove falsy values

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Material Selection
        return !productFormData.materialSelection || productFormData.materialSelection.trim() === "";
      case 2: // Material Grade
        return false; // Material Grade is optional, so always allow proceeding
      case 3: // Batch Size
        return !isPositiveDecimal(productFormData.batchSize) || !Number.isSafeInteger(Number(productFormData.batchSize));
      case 4: // Part Weight and Dimensions
        {
          const d = productFormData.partWeightDimensions || {};
          return ![d.finishWeight, d.partLength, d.partWidth, d.partHeight].every(isPositiveDecimal);
        }
      case 5: // Part Details
        {
          const d = productFormData.partDetails || {};
          const regrindValid = d.regrindPct == null || d.regrindPct === "" ||
            ((isPositiveDecimal(d.regrindPct) || (typeof d.regrindPct === "number" && d.regrindPct === 0 ||
              typeof d.regrindPct === "string" && /^[+]?(?:0+(?:\.0*)?|\.0+)(?:e[+-]?\d+)?$/i.test(d.regrindPct.trim()))) && Number(d.regrindPct) <= 100);
          const cavitiesValid = d.noOfCavities == null || d.noOfCavities === "" ||
            (isPositiveDecimal(d.noOfCavities) && Number.isSafeInteger(Number(d.noOfCavities)));
          return !isPositiveDecimal(d.avgWallThickness) || !regrindValid || !cavitiesValid;
        }
      case 6: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before moving to next step
  const handleStepChange = async (newStep) => {
    // If moving forward from Material Selection step (step 1), validate first
    if (productChatStep === 1 && newStep > productChatStep) {
      if (materialSelectionRef.current && materialSelectionRef.current.validate) {
        const isValid = await materialSelectionRef.current.validate();
        if (!isValid) {
          // Validation failed, don't proceed to next step
          return;
        }
      }
    }
    // If moving forward from Material Grade step (step 2), validate grade if provided
    if (productChatStep === 2 && newStep > productChatStep) {
      if (materialGradeRef.current && materialGradeRef.current.validate && productFormData.materialGrade?.trim()) {
        const isValid = await materialGradeRef.current.validate();
        if (!isValid) {
          // Validation failed, don't proceed to next step
          return;
        }
      }
    }
    // Validation passed or not needed, proceed to next step
    setProductChatStep(newStep);
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      materialGrade: "",
      batchSize: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        regrindPct: "",
        avgWallThickness: "",
        runnerType: "",
        noOfCavities: "",
      },
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setIsMaterialValid(false);
    setIsValidatingMaterial(false);
    setProductChatStep(0);
    setDemoMode(false); // Reset demo toggle button
    if (onDemoModeChange) {
      onDemoModeChange(false); // Notify parent that demo mode is off
    }
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (onDemoModeChange) {
      onDemoModeChange(checked);
    }
    if (checked) {
      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "Bobin",
        materialSelection: "PP",
        materialGrade: "",
        batchSize: "9000",
        partWeightDimensions: {
          finishWeight: "200",
          partLength: "75",
          partWidth: "75",
          partHeight: "90",
        },
        partDetails: {
          regrindPct: "25",
          avgWallThickness: "2",
          runnerType: "Cold runner",
          noOfCavities: "1",
        },
        marketPositioning: "",
        skuWeight: "",
        skuWeightUnit: "g",
        annualVolume: "",
        manufacturingCountry: "China",
      });
      // Navigate to the last step to show all filled values
      setProductChatStep(6);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
      />
      <MaterialSelectionQuestion
        ref={materialSelectionRef}
        value={productFormData.materialSelection}
        onChange={handleMaterialChange}
        onValidationChange={(validationState) => {
          setIsMaterialValid(validationState.isMaterialValid || false);
          setIsValidatingMaterial(validationState.isValidating || false);
        }}
      />
      <MaterialGradePlasticQuestion
        ref={materialGradeRef}
        value={productFormData.materialGrade}
        materialType={productFormData.materialSelection}
        onChange={(value) => setProductFormData({ ...productFormData, materialGrade: value })}
        isMaterialValid={isMaterialValid}
        isValidatingMaterial={isValidatingMaterial}
      />
      <BatchSizeQuestion
        value={productFormData.batchSize}
        onChange={(value) => setProductFormData({ ...productFormData, batchSize: value })}
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partWeightDimensions: value })
        }
      />
      <PartDetailsQuestion
        value={productFormData.partDetails}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partDetails: value })
        }
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
